/**
 * チバリヨ - P1 admin ダッシュボード (Phase 2-B-2.1)
 *
 * Phase 2-B-2.1 の変更点:
 *   - KPI 集計対象を「当月の目標 (goals) が設定されているメンバー」に限定。
 *     粟村 / 松尾(佳) には目標を設定しない運用 → P1 のカード・集計・
 *     アラートから自動除外 (S2 タイムラインには表示される)。
 *
 * 構成:
 *   - A: 当月 KPI サマリー(アポ/見積/訪問件数/全体ペース)
 *   - B: 当期実績(期首〜当月の累計 + 月別振り返り。kpi-fiscal.js 共有モジュール)
 *   - C: アラートセクション(日報未入力 / 訪問途絶 / ペース危険。一番下に表示)
 *   ※ メンバー別実績セクションは廃止 (当期実績のメンバー別カードと重複するため)
 *
 * データソース:
 *   - config/system        : 閾値・期間設定
 *   - members (sales)      : 集計対象メンバー
 *   - clients (active)     : 訪問途絶判定
 *   - reports (当期)       : 実績集計(当月分はここから切り出す)
 *   - goals (当期)         : 目標値(同上)
 *   - holidays             : 営業日カウント補正
 *
 * 設計方針:
 *   - 開いた時に1回 fetch、自動更新なし、手動更新ボタンあり
 *   - 全件メモリ展開でクライアントサイド集計
 */

import {
  collection,
  getDocs,
  doc,
  getDoc
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { db } from './firebase-init.js';
import { fetchVisitStatsByClient, applyVisitStats } from './client-visit-stats.js';
import {
  resolveFiscalPeriod,
  fetchFiscalKpiData,
  buildFiscalRows,
  renderFiscalContentHtml,
  renderFiscalTableHtml,
  fiscalMemberIdSet,
  formatYmJa,
} from './kpi-fiscal.js';

// 訪問途絶日数の既定値(顧客個別の noVisitDays が未設定のときに使う)
const DEFAULT_NO_VISIT_DAYS = 60;

// =====================================================================
// 画面状態
// =====================================================================
let configData = null;
let salesMembers = [];        // 当月の goals があるメンバー (当月 KPI の集計対象)
let allSalesMembers = [];     // sales/active 全員 (当期メンバー別表示用。当月 goals なしでも期内にあれば出す)
let activeClients = [];
let monthReports = [];
let monthGoals = new Map();   // memberId → { appointmentTarget, quoteTarget }
let holidayDates = new Set(); // 当月の休業日(YYYY-MM-DD)
let fiscalPeriod = null;          // 当期情報 (kpi-fiscal.js)
let fiscalReports = [];           // 当期の日報全件 (メンバー絞り込み前)
let fiscalGoalsByMonth = new Map(); // yearMonth → (memberId → goal)

// =====================================================================
// 公開関数
// =====================================================================
export async function renderDashboardPage(container) {
  container.innerHTML = `
    <div class="p1-content">
      <div class="p1-header">
        <h1>ダッシュボード</h1>
        <div class="p1-header-actions">
          <span id="p1-updated" class="p1-updated"></span>
          <button id="p1-refresh-btn" type="button" class="btn btn-ghost">更新</button>
        </div>
      </div>
      <div id="p1-error" class="p1-error" style="display:none;"></div>
      <div id="p1-body">
        <div class="p1-loading">読み込み中…</div>
      </div>
    </div>
  `;

  document.getElementById('p1-refresh-btn').addEventListener('click', loadAndRender);

  await loadAndRender();
}

// =====================================================================
// データロード+描画
// =====================================================================
async function loadAndRender() {
  const body = document.getElementById('p1-body');
  body.innerHTML = '<div class="p1-loading">読み込み中…</div>';

  try {
    await loadAllData();
    renderAll();
    updateTimestamp();
  } catch (err) {
    console.error('[チバリヨ P1] loadAndRender 失敗:', err);
    showError('ダッシュボードの読み込みに失敗しました: ' + err.message);
    body.innerHTML = '';
  }
}

async function loadAllData() {
  // config/system
  const cSnap = await getDoc(doc(db, 'config', 'system'));
  if (!cSnap.exists()) throw new Error('config/system が存在しません');
  configData = cSnap.data();

  // 当月の YYYY-MM
  const now = new Date();
  const yearMonth = formatYearMonth(now);
  const monthStart = `${yearMonth}-01`;
  const monthEnd = formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  // members (sales, active)
  const mSnap = await getDocs(collection(db, 'members'));
  allSalesMembers = mSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((m) => m.role === 'sales' && m.active !== false && m.chibariyoAccess !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  salesMembers = allSalesMembers;

  // clients (全件取得して active を抽出)
  // 最終訪問日/訪問回数は日報(type==='訪問')から導出した実績で上書きする
  // (顧客マスタの lastVisitDate はライブ更新されないため、訪問途絶判定に使えない)
  const [cliSnap, visitStats] = await Promise.all([
    getDocs(collection(db, 'clients')),
    fetchVisitStatsByClient(),
  ]);
  activeClients = cliSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => c.active !== false);
  applyVisitStats(activeClients, visitStats);

  // reports / goals (当期範囲で一括取得 → 当月分を切り出す)
  fiscalPeriod = resolveFiscalPeriod(configData, now);
  const fiscal = await fetchFiscalKpiData(db, fiscalPeriod);
  fiscalReports = fiscal.reports;
  fiscalGoalsByMonth = fiscal.goalsByMonth;

  monthReports = fiscalReports.filter((r) => r.date && r.date.startsWith(yearMonth));
  monthGoals = new Map(fiscalGoalsByMonth.get(yearMonth) || []);

  // Phase 2-B-2.1: KPI 集計対象は「当月の目標が設定されているメンバー」のみ。
  // 粟村 / 松尾(佳) には目標を設定しない運用とし、彼らを
  // メンバー別カード・KPIサマリー・アラートのすべてから自動的に除外する。
  // (日報自体は書けるし S2 タイムラインには表示される。KPI に入らないだけ)
  salesMembers = salesMembers.filter((m) => monthGoals.has(m.id));
  const kpiMemberIds = new Set(salesMembers.map((m) => m.id));
  monthReports = monthReports.filter((r) => kpiMemberIds.has(r.memberId));

  // holidays(当月分のみ)
  const hSnap = await getDocs(collection(db, 'holidays'));
  holidayDates = new Set();
  for (const d of hSnap.docs) {
    const h = d.data();
    // startDate 〜 endDate(または startDate のみ)を当月範囲と突合
    const s = h.startDate;
    const e = h.endDate || h.startDate;
    if (!s) continue;
    // 全日を展開して当月に含まれるものだけ Set に
    const startD = new Date(s);
    const endD = new Date(e);
    for (let d2 = new Date(startD); d2 <= endD; d2.setDate(d2.getDate() + 1)) {
      const str = formatDate(d2);
      if (str >= monthStart && str <= monthEnd) {
        holidayDates.add(str);
      }
    }
  }
}

// =====================================================================
// 全体描画
// =====================================================================
function renderAll() {
  const body = document.getElementById('p1-body');
  body.innerHTML = `
    ${renderKpiSummary()}
    ${renderFiscalSummary()}
    ${renderAlerts()}
  `;
}

// =====================================================================
// C. アラートセクション (表示順は一番下)
// =====================================================================
function renderAlerts() {
  const noReportMembers = computeNoReportMembers();
  const staleClients = computeStaleClients();
  const dangerPaceMembers = computeDangerPaceMembers();

  const hasAny = noReportMembers.length > 0 || staleClients.length > 0 || dangerPaceMembers.length > 0;

  return `
    <section class="p1-section p1-alerts-section">
      <h2 class="p1-section-title">
        ${hasAny ? '⚠️' : '✅'} アラート
      </h2>
      ${hasAny ? '' : '<div class="p1-alert-empty">現在、アラートはありません。順調です。</div>'}

      ${noReportMembers.length > 0 ? `
        <div class="p1-alert-card p1-alert-warning">
          <div class="p1-alert-card-header">
            <span class="p1-alert-icon">📝</span>
            <span class="p1-alert-title">日報未入力</span>
            <span class="p1-alert-count">${noReportMembers.length}名</span>
          </div>
          <div class="p1-alert-card-body">
            ${configData.alertNoReportDays || 3}日以上 日報が入力されていません:
            <ul class="p1-alert-list">
              ${noReportMembers.map((m) => `
                <li>
                  <strong>${escapeHtml(m.name)}</strong>
                  <span class="p1-alert-detail">最終: ${m.lastDate ? escapeHtml(m.lastDate) : '記録なし'}</span>
                </li>
              `).join('')}
            </ul>
          </div>
        </div>
      ` : ''}

      ${staleClients.length > 0 ? `
        <div class="p1-alert-card p1-alert-warning">
          <div class="p1-alert-card-header">
            <span class="p1-alert-icon">🚫</span>
            <span class="p1-alert-title">訪問途絶</span>
            <span class="p1-alert-count">${staleClients.length}社</span>
          </div>
          <div class="p1-alert-card-body">
            設定日数以上 訪問のない顧客:
            <ul class="p1-alert-list p1-alert-list-grid">
              ${staleClients.slice(0, 12).map((c) => `
                <li>
                  <strong>${escapeHtml(c.name || '(名前未設定)')}</strong>
                  <span class="p1-alert-detail">${c.lastVisitDate ? `最終訪問: ${escapeHtml(c.lastVisitDate)}` : '訪問記録なし'}(基準${resolveNoVisitDays(c)}日)</span>
                </li>
              `).join('')}
            </ul>
            ${staleClients.length > 12 ? `<div class="p1-alert-more">他 ${staleClients.length - 12} 社</div>` : ''}
          </div>
        </div>
      ` : ''}

      ${dangerPaceMembers.length > 0 ? `
        <div class="p1-alert-card p1-alert-danger">
          <div class="p1-alert-card-header">
            <span class="p1-alert-icon">⚡</span>
            <span class="p1-alert-title">ペース危険</span>
            <span class="p1-alert-count">${dangerPaceMembers.length}名</span>
          </div>
          <div class="p1-alert-card-body">
            目標達成ペースが ${configData.alertPacePtThreshold || -15}pt 以下:
            <ul class="p1-alert-list">
              ${dangerPaceMembers.map((m) => `
                <li>
                  <strong>${escapeHtml(m.name)}</strong>
                  <span class="p1-alert-detail">
                    アポ: ${formatPt(m.apptPacePt)} / 見積: ${formatPt(m.quotePacePt)}
                  </span>
                </li>
              `).join('')}
            </ul>
          </div>
        </div>
      ` : ''}
    </section>
  `;
}

function computeNoReportMembers() {
  const now = new Date();
  const threshold = configData.alertNoReportDays || 3;
  const thresholdDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - threshold);

  return salesMembers
    .map((m) => {
      const myReports = monthReports.filter((r) => r.memberId === m.id);
      const lastDate = myReports.length > 0
        ? myReports.map((r) => r.date).sort().reverse()[0]
        : null;
      return { ...m, lastDate };
    })
    .filter((m) => {
      if (!m.lastDate) return true;
      const d = new Date(m.lastDate);
      return d < thresholdDate;
    });
}

function computeStaleClients() {
  const now = new Date();

  return activeClients
    .filter((c) => {
      // 顧客個別の訪問途絶日数(未設定なら既定 DEFAULT_NO_VISIT_DAYS)
      const threshold = resolveNoVisitDays(c);
      if (!c.lastVisitDate) return true;
      const thresholdStr = formatDate(
        new Date(now.getFullYear(), now.getMonth(), now.getDate() - threshold)
      );
      return c.lastVisitDate < thresholdStr;
    })
    .sort((a, b) => {
      const av = a.lastVisitDate || '';
      const bv = b.lastVisitDate || '';
      return av.localeCompare(bv);  // 古い順
    });
}

// 顧客個別の訪問途絶日数(正の数なら採用、未設定なら既定 DEFAULT_NO_VISIT_DAYS)
function resolveNoVisitDays(client) {
  const v = client && client.noVisitDays;
  return (typeof v === 'number' && Number.isFinite(v) && v > 0) ? v : DEFAULT_NO_VISIT_DAYS;
}

function computeDangerPaceMembers() {
  const now = new Date();
  const judgeAfter = configData.alertPaceJudgeAfterDay || 15;
  if (now.getDate() < judgeAfter) return [];  // 月初期は判定スキップ

  const threshold = configData.alertPacePtThreshold || -15;

  return salesMembers
    .map((m) => {
      const pace = computeMemberPace(m.id);
      return { ...m, ...pace };
    })
    .filter((m) => {
      const minPt = Math.min(
        m.apptPacePt === null ? Infinity : m.apptPacePt,
        m.quotePacePt === null ? Infinity : m.quotePacePt
      );
      return minPt < threshold;
    });
}

// =====================================================================
// A. 当月 KPI サマリー
// =====================================================================
function renderKpiSummary() {
  const totals = computeOverallTotals();
  const paceInfo = computeOverallPace(totals);

  return `
    <section class="p1-section">
      <h2 class="p1-section-title">📊 当月実績(${formatYearMonth(new Date())})</h2>

      <div class="p1-kpi-grid">
        <div class="p1-kpi-card">
          <div class="p1-kpi-label">アポ獲得</div>
          <div class="p1-kpi-numbers">
            <span class="p1-kpi-actual">${totals.appointment}</span>
            <span class="p1-kpi-divider">/</span>
            <span class="p1-kpi-target">${totals.appointmentTarget}</span>
          </div>
          <div class="p1-kpi-progress">
            ${renderProgressBar(totals.appointment, totals.appointmentTarget)}
            <span class="p1-kpi-rate">${formatRate(totals.appointment, totals.appointmentTarget)}</span>
          </div>
        </div>

        <div class="p1-kpi-card">
          <div class="p1-kpi-label">見積提出</div>
          <div class="p1-kpi-numbers">
            <span class="p1-kpi-actual">${totals.quote}</span>
            <span class="p1-kpi-divider">/</span>
            <span class="p1-kpi-target">${totals.quoteTarget}</span>
          </div>
          <div class="p1-kpi-progress">
            ${renderProgressBar(totals.quote, totals.quoteTarget)}
            <span class="p1-kpi-rate">${formatRate(totals.quote, totals.quoteTarget)}</span>
          </div>
        </div>

        <div class="p1-kpi-card">
          <div class="p1-kpi-label">訪問件数</div>
          <div class="p1-kpi-numbers">
            <span class="p1-kpi-actual">${totals.visit}</span>
            <span class="p1-kpi-unit">件</span>
          </div>
          <div class="p1-kpi-progress p1-kpi-no-bar">
            <span class="p1-kpi-subtext">日報数 ${monthReports.length}件</span>
          </div>
        </div>

        <div class="p1-kpi-card p1-kpi-pace ${paceClass(paceInfo.status)}">
          <div class="p1-kpi-label">全体ペース</div>
          <div class="p1-kpi-numbers">
            <span class="p1-kpi-actual">${paceLabel(paceInfo.status)}</span>
          </div>
          <div class="p1-kpi-progress p1-kpi-no-bar">
            <span class="p1-kpi-subtext">
              営業日 ${paceInfo.elapsed} / ${paceInfo.total} 日
              (${Math.round(paceInfo.elapsed / paceInfo.total * 100)}%)
            </span>
          </div>
        </div>
      </div>
    </section>
  `;
}

function computeOverallTotals() {
  const appointment = monthReports.filter((r) => r.type === '訪問' && r.hasAppointment === true).length;
  const quote = monthReports.filter((r) => r.quote === true).length;
  const visit = monthReports.filter((r) => r.type === '訪問').length;

  let appointmentTarget = 0;
  let quoteTarget = 0;
  for (const [, g] of monthGoals) {
    appointmentTarget += g.appointmentTarget;
    quoteTarget += g.quoteTarget;
  }

  return { appointment, quote, visit, appointmentTarget, quoteTarget };
}

function computeOverallPace(totals) {
  const { elapsed, total } = computeBusinessDayProgress();
  const expectedRatio = total > 0 ? elapsed / total : 0;

  // アポ・見積どちらか悪い方を採用
  const apptPacePt = totals.appointmentTarget > 0
    ? ((totals.appointment / totals.appointmentTarget) - expectedRatio) * 100
    : null;
  const quotePacePt = totals.quoteTarget > 0
    ? ((totals.quote / totals.quoteTarget) - expectedRatio) * 100
    : null;

  let minPt = Infinity;
  if (apptPacePt !== null) minPt = Math.min(minPt, apptPacePt);
  if (quotePacePt !== null) minPt = Math.min(minPt, quotePacePt);

  const status = paceStatus(minPt);
  return { status, elapsed, total, apptPacePt, quotePacePt };
}

// =====================================================================
// B. 当期実績 (期首〜当月の累計 + 月別振り返り)
// =====================================================================
function renderFiscalSummary() {
  if (!fiscalPeriod || fiscalPeriod.months.length === 0) return '';

  // チーム集計 (その月の goals があるメンバーの合算。月ごとに対象を判定)
  const fiscal = buildFiscalRows({
    reports: fiscalReports,
    goalsByMonth: fiscalGoalsByMonth,
    months: fiscalPeriod.months,
    memberId: null,
  });

  const label = fiscalPeriod.fiscalYear ? `第${fiscalPeriod.fiscalYear}期` : '当期';
  const range = `${formatYmJa(fiscalPeriod.startDate.slice(0, 7))}〜${formatYmJa(fiscalPeriod.endDate.slice(0, 7))}`;

  // メンバー別 (期内のいずれかの月に goals があるメンバー)
  const targetIds = fiscalMemberIdSet(fiscalGoalsByMonth);
  const memberCards = allSalesMembers
    .filter((m) => targetIds.has(m.id))
    .map((m) => {
      const f = buildFiscalRows({
        reports: fiscalReports,
        goalsByMonth: fiscalGoalsByMonth,
        months: fiscalPeriod.months,
        memberId: m.id,
      });
      return `
        <div class="p1-fiscal-member-card">
          <div class="p1-fiscal-member-name">${escapeHtml(m.name)}</div>
          ${renderFiscalTableHtml(f, fiscalPeriod.currentYm)}
        </div>
      `;
    }).join('');

  return `
    <section class="p1-section">
      <h2 class="p1-section-title">📈 当期実績(${label}: ${range})</h2>
      <div class="p1-fiscal-card">
        ${renderFiscalContentHtml(fiscal, fiscalPeriod.currentYm)}
      </div>
      ${memberCards ? `<div class="p1-fiscal-members">${memberCards}</div>` : ''}
    </section>
  `;
}

// =====================================================================
// メンバー別ペース計算 (アラートのペース危険判定で使用)
// =====================================================================
function computeMemberPace(memberId) {
  const { elapsed, total } = computeBusinessDayProgress();
  const expectedRatio = total > 0 ? elapsed / total : 0;

  const myReports = monthReports.filter((r) => r.memberId === memberId);
  const appointment = myReports.filter((r) => r.type === '訪問' && r.hasAppointment === true).length;
  const quote = myReports.filter((r) => r.quote === true).length;
  const goal = monthGoals.get(memberId) || { appointmentTarget: 0, quoteTarget: 0 };

  const apptPacePt = goal.appointmentTarget > 0
    ? ((appointment / goal.appointmentTarget) - expectedRatio) * 100
    : null;
  const quotePacePt = goal.quoteTarget > 0
    ? ((quote / goal.quoteTarget) - expectedRatio) * 100
    : null;

  let minPt = Infinity;
  if (apptPacePt !== null) minPt = Math.min(minPt, apptPacePt);
  if (quotePacePt !== null) minPt = Math.min(minPt, quotePacePt);
  if (minPt === Infinity) minPt = null;  // 目標未設定

  const status = minPt === null ? 'unknown' : paceStatus(minPt);
  return { apptPacePt, quotePacePt, status };
}

// =====================================================================
// 営業日カウント
// =====================================================================
function computeBusinessDayProgress() {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  let elapsed = 0;
  let total = 0;
  for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
    if (isBusinessDay(d)) {
      total += 1;
      if (d <= now) elapsed += 1;
    }
  }
  return { elapsed, total };
}

function isBusinessDay(d) {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return false;
  if (holidayDates.has(formatDate(d))) return false;
  return true;
}

// =====================================================================
// ペース判定
// =====================================================================
function paceStatus(pt) {
  // config から閾値取得、フォールバックあり
  const danger = configData.alertPacePtThreshold ?? -15;
  const warn = configData.paceThresholdGood ?? -5;

  if (pt >= warn) return 'good';
  if (pt >= danger) return 'warn';
  return 'danger';
}

function paceLabel(status) {
  switch (status) {
    case 'good':    return '順調';
    case 'warn':    return '注意';
    case 'danger':  return '危険';
    case 'unknown': return '目標未設定';
    default:        return '-';
  }
}

function paceClass(status) {
  return `is-pace-${status}`;
}

// =====================================================================
// プログレスバー
// =====================================================================
function renderProgressBar(actual, target) {
  if (!target || target === 0) {
    return '<div class="p1-progress-bar"><div class="p1-progress-fill is-empty"></div></div>';
  }
  const pct = Math.min(100, Math.round(actual / target * 100));
  let cls = 'is-low';
  if (pct >= 100) cls = 'is-complete';
  else if (pct >= 75) cls = 'is-high';
  else if (pct >= 50) cls = 'is-mid';
  return `
    <div class="p1-progress-bar">
      <div class="p1-progress-fill ${cls}" style="width: ${pct}%"></div>
    </div>
  `;
}

// =====================================================================
// ユーティリティ
// =====================================================================
function updateTimestamp() {
  const el = document.getElementById('p1-updated');
  if (!el) return;
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  el.textContent = `最終更新: ${hh}:${mm}`;
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatYearMonth(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function formatRate(actual, target) {
  if (!target || target === 0) return '—';
  return `${Math.round(actual / target * 100)}%`;
}

function formatPt(pt) {
  if (pt === null || pt === undefined) return '—';
  const sign = pt >= 0 ? '+' : '';
  return `${sign}${pt.toFixed(1)}pt`;
}

function showError(msg) {
  const el = document.getElementById('p1-error');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  setTimeout(() => { el.style.display = 'none'; }, 6000);
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
